/**
 * GuidedTour.jsx — Spark Anywhere v3
 * Spotlight-style guided tour that highlights real UI elements.
 *
 * WIRING — same as before, no changes needed to ChatPanel.jsx wiring.
 *
 * Each step can optionally specify a `targetSelector` (CSS selector).
 * When present the tour:
 *   1. Scrolls the element into view
 *   2. Cuts a spotlight hole in the dark overlay around it
 *   3. Positions the tooltip card near the element with a pointer arrow
 *
 * Steps without a targetSelector show a centred modal card (welcome / finish).
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import UsageLogService from "../services/UsageLogService";

// ─── Tour steps ──────────────────────────────────────────────────────────────
// targetSelector  — CSS selector of the element to highlight
// tooltipPosition — where to place the card relative to the element:
//                   "bottom" | "top" | "left" | "right" | "center"
// padding         — extra space (px) around the spotlight rectangle

const TOUR_STEPS = [
    {
        id:       "welcome",
        icon:     "✦",
        iconBg:   "linear-gradient(135deg, #0891b2, #0e7490)",
        title:    "Welcome to Spark Anywhere",
        subtitle: "Your AI-powered workplace assistant",
        body:     "Spark Anywhere brings Kensho's Spark AI directly into Microsoft Teams — helping you search, create, and collaborate without ever leaving your workflow.\n\nThis tour will walk you through every feature. Click **Next** or use the arrow keys to proceed.",
        tip:      null,
        targetSelector:  null,
        tooltipPosition: "center",
    },
    {
        id:       "modes",
        icon:     "⚡",
        iconBg:   "linear-gradient(135deg, #7c3aed, #6d28d9)",
        title:    "Work · Web · Intranet",
        subtitle: "Switch modes to change what Spark knows",
        body:     "This toggle switches the AI mode:\n\n• **Work** — full toolkit with integrations\n• **Web** — generic AI for open questions\n• **Intranet** — S&P Global intranet search",
        tip:      "💡 Each mode locks a specific Spark AI so answers are always relevant.",
        targetSelector:  "[data-tour='mode-toggle']",
        tooltipPosition: "bottom",
        padding:         8,
    },
    {
        id:       "sidebar-toggle",
        icon:     "☰",
        iconBg:   "linear-gradient(135deg, #0891b2, #0e7490)",
        title:    "Sidebar Toggle",
        subtitle: "Expand or collapse the left panel",
        body:     "Click this button to show or hide the left sidebar. The sidebar contains your **Chat History**, **Create** agents (Word, PowerPoint), and the **Create Spark** option.",
        tip:      "💡 On mobile the sidebar is collapsed by default to give you more screen space.",
        targetSelector:  "[data-tour='sidebar-toggle']",
        tooltipPosition: "bottom",
        padding:         8,
    },
    {
        id:       "spark-store",
        icon:     "🔍",
        iconBg:   "linear-gradient(135deg, #0891b2, #0e7490)",
        title:    "Spark Store",
        subtitle: "6,000+ AI assistants at your fingertips",
        body:     "Click **Spark Store** to browse the full catalog of AI assistants. Each Spark is fine-tuned for a specific domain — corporate policy, SAM integrations, Kensho document library and more.\n\nType **@** in the chat input to quickly switch Sparks mid-conversation.",
        tip:      "💡 The Spark Store syncs automatically and shows 6,000+ sparks from the catalog.",
        targetSelector:  "[data-tour='spark-store-btn']",
        tooltipPosition: "bottom",
        padding:         8,
    },
    {
        id:       "new-chat",
        icon:     "✚",
        iconBg:   "linear-gradient(135deg, #0891b2, #0e7490)",
        title:    "New Chat",
        subtitle: "Start fresh — your history is always saved",
        body:     "Click **+ New Chat** to start a fresh conversation. Your current conversation is automatically saved to the **Chat History** panel on the left before clearing — so nothing is ever lost.",
        tip:      "💡 Your last active chat also persists across page reloads.",
        targetSelector:  "[data-tour='new-chat-btn']",
        tooltipPosition: "bottom",
        padding:         8,
    },
    {
        id:       "composer",
        icon:     "⌨️",
        iconBg:   "linear-gradient(135deg, #059669, #047857)",
        title:    "Message Composer",
        subtitle: "Type, speak, pick a Spark and send",
        body:     "This is where you type your questions. The composer supports:\n\n• Type **@** to switch Sparks inline\n• Type **/** to select Workplace Integrations\n• Click the **mic** button to speak your question\n• Click the **Spark chip** to browse all Sparks",
        tip:      "💡 Select multiple integrations at once to search across Teams, Outlook, OneDrive and SharePoint simultaneously.",
        targetSelector:  "[data-tour='composer']",
        tooltipPosition: "top",
        padding:         10,
    },
    {
        id:       "integrations-chip",
        icon:     "🔗",
        iconBg:   "linear-gradient(135deg, #0078d4, #106ebe)",
        title:    "Workplace Integrations",
        subtitle: "Search across your entire M365 environment",
        body:     "Click **Workplace Integrations** (or type **/**) to search across:\n\n• **Teams** — chat messages and files\n• **Outlook** — your emails\n• **OneDrive** — your documents\n• **SharePoint** — team sites\n• **ADO Boards** — work items and sprints",
        tip:      "💡 Results are grouped by source so you can see where each result came from.",
        targetSelector:  "[data-tour='integrations-chip']",
        tooltipPosition: "top",
        padding:         8,
    },
    {
        id:       "share",
        icon:     "📤",
        iconBg:   "linear-gradient(135deg, #059669, #047857)",
        title:    "Share AI Responses",
        subtitle: "Send responses to where they're needed",
        body:     "Every AI response has a **Share** button (↑ icon). You can:\n\n• 📧 **Email** — send directly via Outlook\n• 💬 **Post to Teams** — any chat or channel\n• 🎯 **Add to ADO** — create a work item\n• ☁️ **Upload to OneDrive** — save as a file",
        tip:      "💡 The download (↓) and copy buttons are also on every message.",
        targetSelector:  "[data-tour='message-actions']",
        tooltipPosition: "left",
        padding:         6,
    },
    {
        id:       "sidebar-create",
        icon:     "📄",
        iconBg:   "linear-gradient(135deg, #d97706, #b45309)",
        title:    "Create Documents",
        subtitle: "Generate Word docs and PowerPoint decks",
        body:     "The **Create** section in the left sidebar has two agents:\n\n• **Word** — generates a fully-formatted Word document\n• **PowerPoint** — generates slides with speaker notes\n\nDescribe what you want in the chat panel and Spark writes it for you. Then save directly to OneDrive.",
        tip:      "💡 You can refine any section by following up in the left chat panel.",
        targetSelector:  "[data-tour='sidebar-create']",
        tooltipPosition: "right",
        padding:         6,
    },
    {
        id:       "create-spark",
        icon:     "🚀",
        iconBg:   "linear-gradient(135deg, #0891b2, #7c3aed)",
        title:    "Create Your Own Spark",
        subtitle: "Build and publish a custom AI assistant",
        body:     "Click **Create Spark** to launch the 3-step wizard:\n\n1. **Basic Info** — name, owner, description\n2. **Parameters** — model, style, temperature, custom instructions\n3. **Discovery** — tags, sample questions, knowledge documents\n\nOnce published, your Spark appears in the Spark Store for everyone.",
        tip:      "💡 Attach OneDrive documents or upload local files to give your Spark domain knowledge.",
        targetSelector:  "[data-tour='create-spark-btn']",
        tooltipPosition: "right",
        padding:         6,
    },
    {
        id:       "finish",
        icon:     "🎉",
        iconBg:   "linear-gradient(135deg, #0891b2, #7c3aed)",
        title:    "You're all set!",
        subtitle: "Start exploring Spark Anywhere",
        body:     "You now know everything you need to get started. Here's a quick recap:\n\n• **Mode toggle** — switch AI context\n• **Spark Store** — browse 6,000+ AI assistants\n• **Integrations** — search M365 in one query\n• **Share** — send responses anywhere\n• **Create** — generate Word docs and PowerPoints\n• **Create Spark** — build your own AI assistant",
        tip:      "💡 You can replay this tour anytime by clicking the **?** button in the header.",
        targetSelector:  null,
        tooltipPosition: "center",
    },
];

// ─── Rich text renderer ───────────────────────────────────────────────────────

const RichBody = ({ text, textSecondary, accent }) => {
    const lines = text.split("\n");
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {lines.map((line, i) => {
                if (!line.trim()) return <div key={i} style={{ height: 3 }} />;
                const isBullet = line.trim().startsWith("•");
                const raw      = isBullet ? line.trim().slice(1).trim() : line;
                const parts    = raw.split(/(\*\*[^*]+\*\*)/g);
                const rendered = parts.map((p, j) =>
                    p.startsWith("**") && p.endsWith("**")
                        ? <strong key={j} style={{ color: accent, fontWeight: 700 }}>{p.slice(2, -2)}</strong>
                        : <span key={j}>{p}</span>
                );
                return (
                    <div key={i} style={{
                        display: "flex", alignItems: "flex-start",
                        gap: isBullet ? 8 : 0,
                        fontSize: 13.5, color: textSecondary, lineHeight: 1.6,
                        paddingLeft: isBullet ? 4 : 0,
                    }}>
                        {isBullet && (
                            <span style={{ color: accent, fontWeight: 800, flexShrink: 0, marginTop: 2, fontSize: 11 }}>▸</span>
                        )}
                        <span>{rendered}</span>
                    </div>
                );
            })}
        </div>
    );
};

// ─── Spotlight hook — measures target element rect ────────────────────────────

function useSpotlight(targetSelector, isOpen, step) {
    const [rect, setRect] = useState(null);
    const pendingRef = useRef(null);

    useEffect(() => {
        // Clear any in-flight timeout from previous step
        if (pendingRef.current) clearTimeout(pendingRef.current);

        if (!isOpen || !targetSelector) {
            setRect(null);
            return;
        }

        const measure = () => {
            const el = document.querySelector(targetSelector);
            if (!el) {
                setRect(null);
                return;
            }
            el.scrollIntoView({ behavior: "smooth", block: "nearest" });

            // ✅ Do NOT clear rect here — keep old rect visible during transition
            // so the overlay doesn't flash to full-dark between steps
            pendingRef.current = setTimeout(() => {
                const r = el.getBoundingClientRect();
                setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
            }, 200); // slightly longer delay so scroll settles first
        };

        measure();
        window.addEventListener("resize", measure);
        return () => {
            window.removeEventListener("resize", measure);
            if (pendingRef.current) clearTimeout(pendingRef.current);
        };
    }, [targetSelector, isOpen, step]);

    return rect;
}

// ─── Tooltip position calculator ──────────────────────────────────────────────

function calcTooltipStyle(rect, position, padding = 8, cardW = 380, cardH = 360) {
    if (!rect || position === "center") {
        return { position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
    }

    const vw  = window.innerWidth;
    const vh  = window.innerHeight;
    const gap = 18;
    const pad = padding;

    const spotTop    = rect.top    - pad;
    const spotLeft   = rect.left   - pad;
    const spotRight  = rect.left   + rect.width  + pad;
    const spotBottom = rect.top    + rect.height + pad;
    const spotCX     = rect.left   + rect.width  / 2;
    const spotCY     = rect.top    + rect.height / 2;

    let style = { position: "fixed" };

    switch (position) {
        case "bottom":
            style.top  = Math.min(spotBottom + gap, vh - cardH - 8);
            style.left = Math.max(8, Math.min(spotCX - cardW / 2, vw - cardW - 8));
            break;
        case "top":
            style.top  = Math.max(8, spotTop - cardH - gap);
            style.left = Math.max(8, Math.min(spotCX - cardW / 2, vw - cardW - 8));
            break;
        case "right":
            style.top  = Math.max(8, Math.min(spotCY - cardH / 2, vh - cardH - 8));
            style.left = Math.min(spotRight + gap, vw - cardW - 8);
            break;
        case "left":
            style.top  = Math.max(8, Math.min(spotCY - cardH / 2, vh - cardH - 8));
            style.left = Math.max(8, spotLeft - cardW - gap);
            break;
        default:
            style.top  = "50%"; style.left = "50%";
            style.transform = "translate(-50%,-50%)";
    }

    return style;
}

function arrowFor(position) {
    const map = { bottom: "up", top: "down", right: "left", left: "right" };
    return map[position] || null;
}

// ─── SVG spotlight overlay ────────────────────────────────────────────────────

const SpotlightOverlay = ({ rect, padding = 8 }) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    if (!rect) {
        return (
            <div style={{
                position: "fixed", inset: 0,
                background: "rgba(0,0,0,0.68)",
                backdropFilter: "blur(3px)",
                WebkitBackdropFilter: "blur(3px)",
            }} />
        );
    }

    const x = Math.max(0, rect.left   - padding);
    const y = Math.max(0, rect.top    - padding);
    const w = rect.width  + padding * 2;
    const h = rect.height + padding * 2;
    const r = 10;

    const cutout = `
        M 0 0 H ${vw} V ${vh} H 0 Z
        M ${x + r} ${y}
        H ${x + w - r} Q ${x + w} ${y} ${x + w} ${y + r}
        V ${y + h - r} Q ${x + w} ${y + h} ${x + w - r} ${y + h}
        H ${x + r} Q ${x} ${y + h} ${x} ${y + h - r}
        V ${y + r} Q ${x} ${y} ${x + r} ${y}
        Z
    `;

    return (
        <svg
            style={{ position: "fixed", inset: 0, pointerEvents: "none" }}
            width={vw} height={vh}
        >
            <defs>
                <clipPath id="gt-clip">
                    <rect x={x} y={y} width={w} height={h} rx={r} />
                </clipPath>
            </defs>
            <path
                d={cutout}
                fill="rgba(0,0,0,0.68)"
                fillRule="evenodd"
                style={{ transition: "d 0.28s cubic-bezier(0.4,0,0.2,1)" }}
            />
            {/* Teal glow ring */}
            <rect x={x - 2} y={y - 2} width={w + 4} height={h + 4}
                rx={r + 2} fill="none"
                stroke="rgba(8,145,178,0.60)" strokeWidth="2.5"
                style={{ transition: "all 0.28s cubic-bezier(0.4,0,0.2,1)" }}
            />
            <rect x={x - 6} y={y - 6} width={w + 12} height={h + 12}
                rx={r + 6} fill="none"
                stroke="rgba(8,145,178,0.18)" strokeWidth="5"
                style={{ transition: "all 0.28s cubic-bezier(0.4,0,0.2,1)" }}
            />
        </svg>
    );
};

// ─── Pointer arrow ────────────────────────────────────────────────────────────

const PointerArrow = ({ direction, accent }) => {
    if (!direction) return null;
    const size = 10;
    const base = {
        position: "absolute", width: 0, height: 0,
        filter: `drop-shadow(0 1px 3px rgba(8,145,178,0.4))`,
    };
    const arrows = {
        up:    { ...base, borderBottom: `${size}px solid ${accent}`, borderLeft: `${size * 0.8}px solid transparent`, borderRight: `${size * 0.8}px solid transparent`, top: -size, left: "50%", transform: "translateX(-50%)" },
        down:  { ...base, borderTop:    `${size}px solid ${accent}`, borderLeft: `${size * 0.8}px solid transparent`, borderRight: `${size * 0.8}px solid transparent`, bottom: -size, left: "50%", transform: "translateX(-50%)" },
        left:  { ...base, borderRight:  `${size}px solid ${accent}`, borderTop: `${size * 0.8}px solid transparent`, borderBottom: `${size * 0.8}px solid transparent`, left: -size, top: "50%", transform: "translateY(-50%)" },
        right: { ...base, borderLeft:   `${size}px solid ${accent}`, borderTop: `${size * 0.8}px solid transparent`, borderBottom: `${size * 0.8}px solid transparent`, right: -size, top: "50%", transform: "translateY(-50%)" },
    };
    return <div style={arrows[direction]} />;
};

// ─── Main component ───────────────────────────────────────────────────────────

const GuidedTour = ({ isOpen, onClose, isDark, isContrast, flags }) => {
    const [step,    setStep]    = useState(0);
    const [animKey, setAnimKey] = useState(0);

    // Filter steps based on active feature flags
    const activeSteps = React.useMemo(() => {
        return TOUR_STEPS.filter(s => {
            if (s.id === "sidebar-create" && !(flags?.contentGeneration)) return false;
            if (s.id === "create-spark"   && !(flags?.createSpark))       return false;
            return true;
        });
    }, [flags?.contentGeneration, flags?.createSpark]);

    const total   = activeSteps.length;
    const current = activeSteps[step];
    const isFirst = step === 0;
    const isLast  = step === total - 1;

    const accent        = "#0891b2";
    const accentDark    = "#0e7490";
    const textColor     = isDark || isContrast ? "#ffffff"                : "#111827";
    const textSecondary = isDark || isContrast ? "rgba(255,255,255,0.75)" : "#374151";
    const textTertiary  = isDark || isContrast ? "rgba(255,255,255,0.38)" : "#9ca3af";
    const surfaceBg     = isDark ? "#16161f" : isContrast ? "#000" : "#ffffff";
    const borderColor   = isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.10)";
    const tipBg         = isDark ? "rgba(8,145,178,0.10)"   : "rgba(8,145,178,0.07)";

    const spotRect = useSpotlight(current.targetSelector, isOpen, step);

    // Track page view for analytics when tour opens
    useEffect(() => {
        if (isOpen) {
            UsageLogService.trackPageView('GuidedTour', {
                totalSteps: activeSteps.length,
                currentStep: step + 1,
                stepId: current.id,
                stepTitle: current.title,
                hasCompletedBefore: localStorage.getItem("spark_tour_done") === "1"
            });
        }
    }, [isOpen, step]);

    useEffect(() => { if (isOpen) { setStep(0); setAnimKey(k => k + 1); } }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const h = (e) => {
            if (e.key === "ArrowRight" || e.key === "Enter") goNext();
            if (e.key === "ArrowLeft")                       goPrev();
            if (e.key === "Escape")                          handleClose();
        };
        window.addEventListener("keydown", h);
        return () => window.removeEventListener("keydown", h);
    }, [isOpen, step]); // eslint-disable-line

    const goToStep = useCallback((t) => { setStep(t); setAnimKey(k => k + 1); }, []);
    const goNext   = useCallback(() => { if (!isLast)  goToStep(step + 1); }, [step, isLast,  goToStep]);
    const goPrev   = useCallback(() => { if (!isFirst) goToStep(step - 1); }, [step, isFirst, goToStep]);

    const handleClose = () => {
        try { localStorage.setItem("spark_tour_done", "1"); } catch { /* ignore */ }
        onClose();
    };

    if (!isOpen) return null;

    const hasTarget = !!current.targetSelector;
    const position  = current.tooltipPosition || "center";
    const padding   = current.padding ?? 8;
    const arrow     = hasTarget ? arrowFor(position) : null;
    const cardWidth = position === "center" ? "min(560px, 92vw)" : "min(390px, 90vw)";

    const tooltipStyle = hasTarget && spotRect
        ? calcTooltipStyle(spotRect, position, padding, 390, 380)
        : { position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)" };

    return (
        <>
            <style>{`
                @keyframes gt-card-in { from { opacity:0; transform:translateY(10px) scale(0.97); } to { opacity:1; transform:translateY(0) scale(1); } }
                @keyframes gt-pulse   { 0%,100% { box-shadow:0 0 0 0 rgba(8,145,178,0.45); } 60% { box-shadow:0 0 0 10px rgba(8,145,178,0); } }
                .gt-next:hover  { transform:scale(1.03) !important; box-shadow:0 6px 22px rgba(8,145,178,0.50) !important; }
                .gt-back:hover  { background: ${isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.07)"} !important; }
                .gt-skip:hover  { color: ${textColor} !important; }
                .gt-dot:hover   { transform: scale(1.4); }
            `}</style>

            {/* ── Overlay layer ── */}
            <div style={{ position: "fixed", inset: 0, zIndex: 10000, pointerEvents: "none" }}>
                <SpotlightOverlay rect={hasTarget ? spotRect : null} padding={padding} />
            </div>

            {/* ── Transparent click-to-close layer behind card ── */}
            <div
                onClick={handleClose}
                style={{ position: "fixed", inset: 0, zIndex: 10001, cursor: "default" }}
            />

            {/* ── Tooltip card ── */}
            <div
                key={animKey}
                onClick={e => e.stopPropagation()}
                style={{
                    ...tooltipStyle,
                    zIndex: 10002,
                    width: cardWidth,
                    borderRadius: 16,
                    transition: "top 0.28s cubic-bezier(0.4,0,0.2,1), left 0.28s cubic-bezier(0.4,0,0.2,1)",
                    background:    surfaceBg,
                    border:        `1px solid ${borderColor}`,
                    boxShadow:     isDark
                        ? "0 20px 70px rgba(0,0,0,0.75), 0 0 0 1px rgba(255,255,255,0.05)"
                        : "0 20px 70px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)",
                    display:       "flex",
                    flexDirection: "column",
                    overflow:      "visible",
                    animation:     "gt-card-in 0.28s cubic-bezier(0.16,1,0.3,1)",
                    maxHeight:     "85vh",
                }}
            >
                {/* Arrow pointer */}
                <div style={{ position: "relative" }}>
                    <PointerArrow direction={arrow} accent={accent} />
                </div>

                {/* Scrollable inner */}
                <div style={{ overflowY: "auto", borderRadius: 16, display: "flex", flexDirection: "column" }}>

                    {/* Accent bar */}
                    <div style={{ height: 3, flexShrink: 0, background: `linear-gradient(90deg, ${accent}, #7c3aed, ${accentDark})` }} />

                    {/* Content */}
                    <div style={{ padding: "22px 26px 18px" }}>

                        {/* Step badge + close */}
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
                            <div style={{
                                display: "inline-flex", alignItems: "center", gap: 7,
                                padding: "3px 11px", borderRadius: 999,
                                background: isDark ? "rgba(8,145,178,0.13)" : "rgba(8,145,178,0.08)",
                                border: "1px solid rgba(8,145,178,0.22)",
                                fontSize: 11, fontWeight: 700, color: accent, letterSpacing: "0.3px",
                            }}>
                                <span style={{
                                    width: 16, height: 16, borderRadius: "50%",
                                    background: accent, color: "#fff",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    fontSize: 9, fontWeight: 800, flexShrink: 0,
                                }}>{step + 1}</span>
                                of {total}
                            </div>
                            <button type="button" onClick={handleClose} style={{
                                border: "none", background: "transparent", cursor: "pointer",
                                color: textTertiary, fontSize: 18, padding: "3px 7px",
                                borderRadius: 7, lineHeight: 1, transition: "all 0.13s",
                            }}
                                onMouseEnter={e => { e.currentTarget.style.color = textColor; e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"; }}
                                onMouseLeave={e => { e.currentTarget.style.color = textTertiary; e.currentTarget.style.background = "transparent"; }}
                            >✕</button>
                        </div>

                        {/* Icon + heading */}
                        <div style={{ display: "flex", alignItems: "flex-start", gap: 13, marginBottom: 14 }}>
                            <div style={{
                                width: 46, height: 46, borderRadius: 13, flexShrink: 0,
                                background: current.iconBg,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: 19,
                                boxShadow: "0 3px 14px rgba(8,145,178,0.30)",
                                animation: (step === 0 || step === total - 1) ? "gt-pulse 2s ease-in-out infinite" : "none",
                            }}>
                                {current.icon}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: position === "center" ? 19 : 16, fontWeight: 800, color: textColor, letterSpacing: "-0.3px", lineHeight: 1.2 }}>
                                    {current.title}
                                </div>
                                <div style={{ fontSize: 12, color: accent, marginTop: 4, fontWeight: 600 }}>
                                    {current.subtitle}
                                </div>
                            </div>
                        </div>

                        {/* Divider */}
                        <div style={{ height: 1, background: borderColor, marginBottom: 13 }} />

                        {/* Body */}
                        <RichBody text={current.body} textSecondary={textSecondary} accent={accent} />

                        {/* Tip */}
                        {current.tip && (
                            <div style={{
                                marginTop: 13, padding: "9px 12px", borderRadius: 9,
                                background: tipBg,
                                border: `1px solid rgba(8,145,178,0.18)`,
                                borderLeft: `3px solid ${accent}`,
                                fontSize: 12.5, color: textSecondary, lineHeight: 1.6,
                            }}>
                                {current.tip}
                            </div>
                        )}

                        {/* Quick-jump grid — welcome step only */}
                        {step === 0 && (
                            <div style={{ marginTop: 18, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 7 }}>
                                {activeSteps.slice(1, 7).map((s, i) => (
                                    <div key={s.id} onClick={() => goToStep(i + 1)} style={{
                                        padding: "9px 7px", borderRadius: 9,
                                        background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
                                        border: `1px solid ${borderColor}`,
                                        cursor: "pointer", textAlign: "center", transition: "all 0.14s",
                                    }}
                                        onMouseEnter={e => { e.currentTarget.style.background = isDark ? "rgba(8,145,178,0.12)" : "rgba(8,145,178,0.07)"; e.currentTarget.style.borderColor = `${accent}40`; }}
                                        onMouseLeave={e => { e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)"; e.currentTarget.style.borderColor = borderColor; }}
                                    >
                                        <div style={{ fontSize: 17, marginBottom: 3 }}>{s.icon}</div>
                                        <div style={{ fontSize: 10, fontWeight: 700, color: textSecondary, lineHeight: 1.3 }}>{s.title}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div style={{
                        padding: "11px 26px 18px",
                        borderTop: `1px solid ${borderColor}`,
                        background: isDark ? "rgba(0,0,0,0.14)" : "rgba(0,0,0,0.015)",
                        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                    }}>
                        {/* Progress dots */}
                        <div style={{ display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap" }}>
                            {activeSteps.map((_, i) => (
                                <button key={i} type="button" className="gt-dot"
                                    onClick={() => goToStep(i)}
                                    title={activeSteps[i].title}
                                    style={{
                                        width: i === step ? 18 : 6, height: 6,
                                        borderRadius: 3, border: "none", padding: 0, cursor: "pointer",
                                        background: i === step
                                            ? accent : i < step
                                            ? `${accent}55`
                                            : isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)",
                                        transition: "all 0.28s cubic-bezier(0.4,0,0.2,1)",
                                        flexShrink: 0,
                                    }}
                                />
                            ))}
                        </div>

                        {/* Buttons */}
                        <div style={{ display: "flex", gap: 7, alignItems: "center", flexShrink: 0 }}>
                            {!isLast && (
                                <button type="button" className="gt-skip" onClick={handleClose} style={{
                                    padding: "7px 13px", borderRadius: 7, fontSize: 12, fontWeight: 500,
                                    border: "none", background: "transparent", color: textTertiary,
                                    cursor: "pointer", transition: "color 0.13s",
                                }}>Skip</button>
                            )}
                            {!isFirst && (
                                <button type="button" className="gt-back" onClick={goPrev} style={{
                                    padding: "7px 15px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                                    border: `1px solid ${borderColor}`,
                                    background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                                    color: textColor, cursor: "pointer",
                                    display: "flex", alignItems: "center", gap: 5, transition: "all 0.13s",
                                }}>
                                    <span style={{ fontSize: 15 }}>‹</span> Back
                                </button>
                            )}
                            <button type="button" className="gt-next" onClick={isLast ? handleClose : goNext} style={{
                                padding: "8px 20px", borderRadius: 8, fontSize: 13, fontWeight: 700,
                                border: "none",
                                background: `linear-gradient(135deg, ${accent}, ${accentDark})`,
                                color: "#fff", cursor: "pointer",
                                display: "flex", alignItems: "center", gap: 6,
                                boxShadow: `0 3px 14px rgba(8,145,178,0.38)`,
                                transition: "all 0.13s",
                            }}>
                                {isLast ? <>✦ Get Started</> : <>Next <span style={{ fontSize: 15 }}>›</span></>}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default GuidedTour;
